import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { omit, get } from "lodash";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useFormik } from "formik";

import {
  useLazyApplicationDetailQuery,
  usePostApplicationDetailsMutation,
} from "apis/ApplicationRequestDetail";
import { useFloorQuery } from "apis/Floor";

//import Components
import ApplicantDetails from "Components/ApplicantDetails";
import Stepper from "common/Stepper";
import ConnectionLoadDetails from "pages/Consumer/ApplicationType/TemporaryConnection/ConnectionLoadDetails";
import BreadCrumb from "Components/Common/BreadCrumb";
import { applicationTypes, minHtContractDemandLoad } from "common/constant";
import Declaration from "Components/Declaration";
import AddressForPower from "Components/AddressForPower";
import AuthorizedPersonDetails from "Components/AuthorizedPersonDetails/AuthorizedPersonDetails";
import DocumentToSubmit from "Components/DocumentsToSubmit";
import Backdrop from "Components/Common/Backdrop";
import { myRequestRoute } from "Routes/constants";

import {
  generateFormData,
  filterEmptyStrings,
  convertDocumentKeys,
} from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { setValues } from "slices/temporaryConnection/reducer";
import { useTemporaryConnectionValidationSchema } from "hooks";
import { getIsDocumentRequired } from "utils/getIsDocumentRequired";

const temporaryConnectionValues = [
  { keyName: "id", defaultValue: 0 },
  { keyName: "applicant.id", defaultValue: 0 },
  { keyName: "authorizedPerson.id", defaultValue: 0 },
  { keyName: "connectionLoad.id", defaultValue: 0 },
  { keyName: "utilityAddress.id", defaultValue: 0 },
  { keyName: "declaration.id", defaultValue: 0 },
  { keyName: "documents", defaultValue: [] },
];

// const stepDetails = {
//   1: "applicant",
//   2: "authorizedPerson",
//   3: "utilityAddress",
//   4: "connectionLoad",
//   5: "documents",
//   6: "declaration",
// };

const TemporaryConnection = ({ applicationId, handleClose = () => {}, t }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const { data: { data: floors } = {} } = useFloorQuery();

  const [
    getApplicationData,
    {
      data: { data: applicationData } = {},
      isLoading: isApplicationDataLoading,
    },
  ] = useLazyApplicationDetailQuery();

  const navigate = useNavigate();
  const user = useSelector((state) => state.Login.user);
  const [submitApplication, { isLoading }] =
    usePostApplicationDetailsMutation();

  const getValidationSchema = useTemporaryConnectionValidationSchema();

  const dispatch = useDispatch();

  const floorOptions = useMemo(
    () =>
      floors?.map((floor) => ({ label: floor.name, value: floor.id })) ?? [],
    [floors]
  );

  const initialValues = useMemo(
    () => ({
      id: applicationData?.id ?? 0,
      utilityId: 1,
      applicationTypeId: applicationTypes["Temporary Connection"],
      applicant: {
        id: applicationData?.applicant?.id ?? 0,
        applicantTypeId: applicationData?.applicant?.applicantType?.id ?? "",
        applicantName: applicationData?.applicant?.applicantName ?? "",
        panNumber: applicationData?.applicant?.panNumber ?? "",
        gstNumber: applicationData?.applicant?.gstNumber ?? "",
        occupancyTypeId: applicationData?.applicant?.occupancyType?.id ?? "",
        addressType: applicationData?.applicant?.addressType ?? "",
        correspondenceAddress:
          applicationData?.applicant?.correspondenceAddress ?? "",
        isGSTRegistered: applicationData?.applicant?.isGSTRegistered ?? "",
        city: applicationData?.applicant?.city ?? "",
        pinCode: applicationData?.applicant?.pinCode ?? "",
        email: applicationData?.applicant?.email ?? "",
        // isEmailVerified: !!applicationData?.applicant?.email ?? false,
        mobileNumber: applicationData?.applicant?.mobileNumber ?? "",
        landLine: applicationData?.applicant?.landLine ?? "",
        alternateEmail: applicationData?.applicant?.alternateEmail
          ? applicationData?.applicant?.alternateEmail?.split(", ")
          : [],
        alternateMobileNumber:
          applicationData?.applicant?.alternateMobileNumber ?? "",
        eBillConfirmation: true,
      },
      authorizedPerson: {
        isAuthorizedPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email ===
              applicationData.user.email &&
            applicationData.authorizedPerson.mobileNumber ===
              applicationData.user.mobileNumber
          : true,
        id: applicationData?.authorizedPerson?.id ?? 0,
        salutation: applicationData?.authorizedPerson?.salutation ?? "",
        firstName: applicationData?.authorizedPerson?.firstName ?? "",
        middleName: applicationData?.authorizedPerson?.middleName ?? "",
        lastName: applicationData?.authorizedPerson?.lastName ?? "",
        designation: applicationData?.authorizedPerson?.designation ?? "",
        email: applicationData?.authorizedPerson?.email ?? "",
        mobileNumber: applicationData?.authorizedPerson?.mobileNumber ?? "",
        landLine: applicationData?.authorizedPerson?.landLine ?? "",
        image: "",
        imageUrl: applicationData?.authorizedPerson?.imageUrl ?? "",
        isUnRegisteredPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email !== user.email &&
            applicationData.authorizedPerson.mobileNumber !== user.mobileNumber
          : false,
        isEditablePerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email !== user.email &&
            applicationData.authorizedPerson.mobileNumber !== user.mobileNumber
          : false,
      },
      connectionLoad: {
        id: applicationData?.connectionLoad?.id ?? 0,
        powerRequirementUnit:
          applicationData?.connectionLoad?.powerRequirementUnit ?? "",
        loadDetails: applicationData?.connectionLoad?.loadDetail ?? "",
        supplyVoltage: applicationData?.connectionLoad?.supplyVoltage
          ? +applicationData?.connectionLoad?.supplyVoltage
          : "",
        supplyVoltageUnit:
          applicationData?.connectionLoad?.supplyVoltageUnit ?? "",
        powerRequirement:
          applicationData?.connectionLoad?.powerRequirement ?? "",
        timeShift: applicationData?.connectionLoad?.timeShiftEnum ?? "",
        tention: applicationData?.connectionLoad?.powerRequirement
          ? applicationData.connectionLoad.powerRequirement <
            minHtContractDemandLoad
            ? "LT"
            : "HT"
          : "",
        isGreenPowerRequired: applicationData?.connectionLoad?.greenPower
          ? true
          : false,
        greenPower: applicationData?.connectionLoad?.greenPower ?? "",
        averageConsumption:
          applicationData?.connectionLoad?.averageConsumption ?? "",
        expectedConnectionStartDate: applicationData?.connectionLoad
          ?.expectedConnectionStartDate
          ? convertUTCDateToLocalDate(
              applicationData.connectionLoad.expectedConnectionStartDate
            )
          : "",

        expectedConnectionEndDate: applicationData?.connectionLoad
          ?.expectedConnectionEndDate
          ? convertUTCDateToLocalDate(
              applicationData.connectionLoad.expectedConnectionEndDate
            )
          : "",
      },
      utilityAddress: {
        id: applicationData?.utilityAddress?.id ?? 0,
        premiseTypeId: applicationData?.utilityAddress?.premiseType?.id ?? "",
        premiseUsagePurpose:
          applicationData?.utilityAddress?.premiseUsagePurpose ?? "",
        premisesAreaId: applicationData?.utilityAddress?.premiseArea?.id ?? "",
        zoneName: applicationData?.utilityAddress?.zone?.name ?? "",
        otherZone: "",
        blockName: applicationData?.utilityAddress?.block?.name ?? "",
        otherBlock: "",
        roadName: applicationData?.utilityAddress?.road?.name ?? "",
        otherRoad: "",
        buildingName: applicationData?.utilityAddress?.building?.name ?? "",
        otherBuilding: "",
        floors: [],
        unitName: applicationData?.utilityAddress?.unitName ?? "",
        nearestLandmark: applicationData?.utilityAddress?.nearestLandmark ?? "",
        isBPL: applicationData?.utilityAddress?.isBPL ?? "",
        // isRural: applicationData?.utilityAddress?.isRural ?? "",
      },
      declaration: {
        declarationId: applicationData?.declaration?.id ?? 0,
        isConsumerDuesOutstanding:
          !!applicationData?.declaration?.consumerDuesOutstanding ?? false,
        consumerDuesOutstanding:
          applicationData?.declaration?.consumerDuesOutstanding ?? "",
        isPremisesDuesOutstanding:
          !!applicationData?.declaration?.premisesDuesOutstanding ?? false,
        premisesDuesOutstanding:
          applicationData?.declaration?.premisesDuesOutstanding ?? "",
        isFirmDuesOutstanding:
          !!applicationData?.declaration?.firmDuesOutstanding ?? false,
        firmDuesOutstanding:
          applicationData?.declaration?.firmDuesOutstanding ?? "",
      },
      documents: applicationData?.documents?.filter(({ id }) => id) ?? [],
      extraValues: {
        requiredDocumentsCount: applicationData?.requiredDocumentsCount ?? 0,
        proofOfOwnershipTypes: [],
        userMobileNumber: applicationData?.user?.mobileNumber ?? "",
        userEmail: applicationData?.user?.email ?? "",
      },
      proofOfOwnershipDescription: "",

      isDraft: true,
    }),
    [applicationData]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: getValidationSchema(currentStep),

    onSubmit: async (values, actions) => {
      if (currentStep === steps.length - 1) {
        createTemporaryConnection(values);
      } else {
        setCurrentStep((prev) => prev + 1);
        actions.setTouched({});
      }
    },
  });
  const { setFieldValue, errors, values, handleSubmit, setFieldTouched } =
    formik;

  const {
    documents,
    extraValues: { requiredDocumentsCount },
  } = values;

  const steps = useMemo(
    () => [
      {
        id: 1,
        name: t("Applicant Details"),
        isCompleted: currentStep >= 1,
        component: <ApplicantDetails formik={formik} />,
      },
      {
        id: 2,
        name: t("Authorized Person Details"),
        isCompleted: currentStep >= 2,
        component: <AuthorizedPersonDetails formik={formik} />,
      },
      {
        id: 3,
        name: t("Address for Power Connection"),
        isCompleted: currentStep >= 3,
        component: (
          <AddressForPower formik={formik} floorOptions={floorOptions} />
        ),
      },
      {
        id: 4,
        name: t("Connection Load Details"),
        isCompleted: currentStep >= 4,
        component: <ConnectionLoadDetails formik={formik} />,
      },
      {
        id: 5,
        name: t("Document to Submit"),
        isCompleted: currentStep >= 5,
        component: <DocumentToSubmit formik={formik} />,
      },
      {
        id: 6,
        name: t("Declaration"),
        isCompleted: currentStep === 6,
        component: <Declaration formik={formik} />,
      },
    ],
    [currentStep, formik, floorOptions]
  );

  const validateDocument = () => {
    const requiredUploadedDocumentsCount = documents.filter(
      ({ documentRequiredEnum }) => getIsDocumentRequired(documentRequiredEnum)
    ).length;

    if (
      currentStep === 4 &&
      requiredUploadedDocumentsCount !== requiredDocumentsCount
    ) {
      toast.error("Please upload all required documents");
    }
  };

  const createTemporaryConnection = async (values, isDraft = false) => {
    const imageKeyToRemove = values.authorizedPerson.image
      ? "authorizedPerson.imageUrl"
      : "authorizedPerson.image";

    const otherZone = values.utilityAddress.otherZone;
    const otherBlock = values.utilityAddress.otherBlock;
    const otherRoad = values.utilityAddress.otherRoad;
    const otherBuilding = values.utilityAddress.otherBuilding;
    const cityId = values.applicant.city?.id ?? "";

    // converts document array to key-value pair
    const payloadWithUpdatedDocumentKeys = convertDocumentKeys(values);

    const { expectedConnectionStartDate, expectedConnectionEndDate } =
      payloadWithUpdatedDocumentKeys.connectionLoad;

    const applicationDetails = omit(
      {
        ...payloadWithUpdatedDocumentKeys,
        applicant: {
          ...payloadWithUpdatedDocumentKeys.applicant,
          panNumber:
            payloadWithUpdatedDocumentKeys.applicant.panNumber.toUpperCase(),
          gstNumber:
            payloadWithUpdatedDocumentKeys.applicant.gstNumber.toUpperCase(),
          alternateEmail: payloadWithUpdatedDocumentKeys.applicant
            .alternateEmail.length
            ? payloadWithUpdatedDocumentKeys.applicant.alternateEmail.join(", ")
            : "",
          cityId,
        },
        connectionLoad: {
          ...payloadWithUpdatedDocumentKeys.connectionLoad,
          expectedConnectionStartDate: expectedConnectionStartDate
            ? expectedConnectionStartDate.format("YYYY-MM-DD")
            : "",
          expectedConnectionEndDate: expectedConnectionEndDate
            ? expectedConnectionEndDate.format("YYYY-MM-DD")
            : "",
        },
        utilityAddress: {
          ...payloadWithUpdatedDocumentKeys.utilityAddress,
          zoneName:
            payloadWithUpdatedDocumentKeys.utilityAddress.zoneName === "other"
              ? otherZone
              : payloadWithUpdatedDocumentKeys.utilityAddress.zoneName,
          blockName:
            payloadWithUpdatedDocumentKeys.utilityAddress.blockName === "other"
              ? otherBlock
              : payloadWithUpdatedDocumentKeys.utilityAddress.blockName,
          roadName:
            payloadWithUpdatedDocumentKeys.utilityAddress.roadName === "other"
              ? otherRoad
              : payloadWithUpdatedDocumentKeys.utilityAddress.roadName,
          buildingName:
            payloadWithUpdatedDocumentKeys.utilityAddress.buildingName ===
            "other"
              ? otherBuilding
              : payloadWithUpdatedDocumentKeys.utilityAddress.buildingName,
          floors: payloadWithUpdatedDocumentKeys.utilityAddress.floors.map(
            ({ label, value }) => ({
              id: value,
              name: label,
            })
          ),
        },
        isDraft,
      },
      [
        // "applicant.isEmailVerified",
        "applicant.city",
        "utilityAddress.otherZone",
        "utilityAddress.otherBlock",
        "utilityAddress.otherRoad",
        "utilityAddress.otherBuilding",
        "connectionLoad.tention",
        "connectionLoad.isGreenPowerRequired",
        "connectionLoad.backupRequired",
        "authorizedPerson.isAuthorizedPerson",
        "authorizedPerson.isEditablePerson",
        "authorizedPerson.isUnRegisteredPerson",
        "declaration.isConsumerDuesOutstanding",
        "declaration.isPremisesDuesOutstanding",
        "declaration.isFirmDuesOutstanding",
        "proofOfOwnershipDescription",
        "documents",
        "extraValues",
        imageKeyToRemove,
      ]
    );

    const filteredValues = filterEmptyStrings(applicationDetails);

    // remove entries that contain only id field
    const finalPayload = Object.entries(filteredValues).reduce(
      (res, [key, value]) => {
        if (
          typeof value !== "object" ||
          value instanceof File ||
          Object.keys(value).length > 1
        ) {
          return { ...res, [key]: value };
        }
        return res;
      },
      {}
    );

    const formData = new FormData();

    generateFormData(formData, {
      ...finalPayload,
      declaration: filteredValues.declaration,
    });

    try {
      const res = await submitApplication(formData);
      if (isDraft) {
        temporaryConnectionValues.forEach(({ keyName, defaultValue }) =>
          setFieldValue(keyName, get(res.data, keyName, defaultValue))
        );
        setFieldValue("proofOfOwnershipDescription", "");
      } else {
        handleClose();

        // If submitted then navigate to my-request
        if (!applicationData) {
          navigate(myRequestRoute);
        }
      }
    } catch (error) {}
  };

  const handleDraft = async () => {
    // if (values.applicant.isEmailVerified) {
    const declarationFields = [
      {
        value: values.declaration.isConsumerDuesOutstanding,
        key: "declaration.consumerDuesOutstanding",
      },
      {
        value: values.declaration.isPremisesDuesOutstanding,
        key: "declaration.premisesDuesOutstanding",
      },
      {
        value: values.declaration.isFirmDuesOutstanding,
        key: "declaration.firmDuesOutstanding",
      },
    ];
    if (currentStep === steps.length - 1) {
      declarationFields.forEach(({ value, key }) => {
        if (value) {
          setFieldTouched(key, true);
        }
      });
      if (!errors.declaration) {
        createTemporaryConnection(values, true);
      }
    } else if (
      currentStep === steps.length - 2 &&
      values.documents.some(
        ({ documentTypeId }) =>
          !documentTypeId && !values.proofOfOwnershipDescription
      )
    ) {
      toast.error("Please enter other document description");
    } else {
      createTemporaryConnection(values, true);
    }
    // } else {
    //   toast.error("Please verify your email");
    // }
  };

  const getApplicationDataByApplicationId = async (applicationId) => {
    try {
      await getApplicationData(applicationId);
    } catch (error) {}
  };

  // set initial floors
  useEffect(() => {
    const initialFloors = applicationData?.utilityAddress?.floors || [];

    if (floorOptions.length && initialFloors.length) {
      setFieldValue(
        "utilityAddress.floors",
        floorOptions.filter((option) =>
          initialFloors.some((floor) => floor.id === option.value)
        )
      );
    }
  }, [floorOptions, applicationData]);

  useEffect(() => {
    dispatch(setValues(values));
  }, [values, dispatch]);

  useEffect(() => {
    if (applicationId) {
      getApplicationDataByApplicationId(applicationId);
    }
  }, [applicationId]);

  // useEffect(() => {
  //   if (applicationData) {
  //     let completedSteps = null;
  //     for (const key in stepDetails) {
  //       const value = applicationData[stepDetails[key]];
  //       if (!value || (Array.isArray(value) && value.length === 0)) {
  //         completedSteps = key - 1;
  //         break;
  //       }
  //     }
  //     if (completedSteps === null) {
  //       completedSteps = 6;
  //     }
  //     setCompletedSteps(completedSteps);
  //   }
  // }, [applicationData]);

  return (
    <div className={!applicationData ? "h-100" : ""}>
      <Backdrop open={isApplicationDataLoading} />
      {!applicationData && (
        <BreadCrumb
          title={t("Temporary Connection")}
          pageTitle={t("Power Applications")}
        />
      )}
      <div className="stepper-container">
        <Stepper
          steps={steps}
          handleSubmit={handleSubmit}
          handleDraft={handleDraft}
          activeStepIndex={currentStep}
          setActiveStepIndex={setCurrentStep}
          validateDocument={validateDocument}
          isLoading={isLoading}
          formik={formik}
          isDraftDisabled={
            !!applicationId &&
            applicationData?.applicationStatus?.name?.toLowerCase() !== "saved"
          }
        />
      </div>
    </div>
  );
};

TemporaryConnection.propTypes = {
  applicationId: PropTypes.number,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(TemporaryConnection);
